// In-app replacement for window.confirm() (UI audit, Oct 12 2026).
//
//   if (!(await confirmDialog({ message: "Delete this note?", danger: true }))) return;
//
// Same module-level-store shape as utils/toast.js so it works from any handler
// without threading a hook. <ConfirmHost /> (mounted once in Layout) renders it.
// Resolves true on confirm, false on cancel / Escape / backdrop click.
// Only one dialog at a time: a second call while one is open cancels the first.

let current = null;
const listeners = new Set();

function emit() {
  for (const l of listeners) l(current);
}

export function subscribeConfirm(listener) {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}

export function confirmDialog({
  title = "Are you sure?",
  message = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
} = {}) {
  if (current) current.resolve(false);
  return new Promise((resolve) => {
    current = {
      title, message, confirmLabel, cancelLabel, danger,
      resolve: (value) => {
        current = null;
        emit();
        resolve(value);
      },
    };
    emit();
  });
}

/**
 * The shape the per-page confirm modals all used: a sentence like
 * "Delete Visit #3? This cannot be undone." and a callback to run on confirm.
 * Accepts `confirmThen(message, fn, label)` or the old state object
 * `confirmThen({ message | label, onConfirm })`. The first question becomes the
 * dialog title and the rest its message. Always styled as destructive.
 */
export function confirmThen(messageOrObj, fn, confirmLabel = "Delete") {
  let message = messageOrObj;
  let onConfirm = fn;
  if (messageOrObj && typeof messageOrObj === "object") {
    message = messageOrObj.message ?? messageOrObj.label ?? "";
    onConfirm = messageOrObj.onConfirm;
    confirmLabel = messageOrObj.confirmLabel ?? confirmLabel;
  }
  const text = String(message ?? "");
  const q = text.indexOf("? ");
  const title = q > 0 ? text.slice(0, q + 1) : text || "Are you sure?";
  const body = q > 0 ? text.slice(q + 2) : "";
  return confirmDialog({ title, message: body, confirmLabel, danger: true }).then((ok) =>
    ok && onConfirm ? onConfirm() : undefined,
  );
}
